// push — register for notifications, and report the token.
//
// This addon deliberately stops at the token. Sending is a server concern with
// a credential attached, and an addon that shipped a sender would either need
// your APNs key in the app bundle or a hosted relay nobody asked for.
//
// It is inert until the native projects are configured: no permission prompt
// fires, no listener registers, and nothing reaches the network. That is the
// same "dormant, not absent" rule the auth addon follows, and it matters more
// here — an unprompted notification permission dialog on first launch is the
// fastest way to be permanently denied.

import { plugin, isNative } from '../../src/core/native.js';
import { bus, setState } from '../../src/core/state.js';
import { toast } from '../../src/core/util.js';

const TOKEN_KEY = 'push.token';

let registered = false;

function pushPlugin() {
  return isNative ? plugin('PushNotifications') : null;
}

/** Whether this build can register at all. */
export function pushAvailable() {
  return !!pushPlugin()?.register;
}

/** The last token this device was issued, or null. */
export function deviceToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Ask for permission and register.
 *
 * Call this from a control the person actually pressed — never from boot. The
 * OS gives you exactly one prompt, and spending it before the person knows what
 * the notifications are for is how apps end up unable to send any.
 *
 * @returns {Promise<'granted'|'denied'|'unavailable'>}
 */
export async function enablePush() {
  const Push = pushPlugin();
  if (!Push?.register) return 'unavailable';

  const status = await Push.checkPermissions?.() ?? { receive: 'prompt' };
  const decision = status.receive === 'prompt' || status.receive === 'prompt-with-rationale'
    ? await Push.requestPermissions()
    : status;

  if (decision.receive !== 'granted') {
    setState({ pushEnabled: false });
    return 'denied';
  }

  attachListeners(Push);
  await Push.register();
  setState({ pushEnabled: true });
  return 'granted';
}

function attachListeners(Push) {
  if (registered) return;
  registered = true;

  Push.addListener('registration', (token) => {
    // Kept locally so whatever sends the notifications can collect it later —
    // this addon does not know where your backend is, and guessing would be
    // worse than making you wire it.
    try {
      localStorage.setItem(TOKEN_KEY, token.value);
    } catch { /* private mode — the token is still emitted below */ }
    bus.emit('push:token', token.value);
  });

  Push.addListener('registrationError', (err) => {
    bus.emit('push:error', err);
  });

  Push.addListener('pushNotificationReceived', (notification) => {
    // Foreground delivery is silent on both platforms. Surfacing it is the
    // app's decision, so this only announces it.
    bus.emit('push:received', notification);
  });

  Push.addListener('pushNotificationActionPerformed', (action) => {
    bus.emit('push:opened', action.notification);
  });
}

/** Stop delivering to this device. The permission itself is the OS's to revoke. */
export async function disablePush() {
  const Push = pushPlugin();
  if (!Push?.unregister) return;
  await Push.unregister();
  try {
    localStorage.removeItem(TOKEN_KEY);
  } catch { /* nothing to clear */ }
  setState({ pushEnabled: false });
}

export default {
  id: 'push',

  async boot() {
    // Re-attach listeners for a device that already granted permission, so a
    // notification tapped from the lock screen still routes. No prompt, no
    // register call — this only picks up a decision already made.
    const Push = pushPlugin();
    if (!Push?.checkPermissions) return;
    const status = await Push.checkPermissions();
    if (status.receive !== 'granted') return;
    attachListeners(Push);
    await Push.register();
    setState({ pushEnabled: true });
  },

  settingsRow() {
    // No control at all when this build cannot register — a switch that does
    // nothing is worse than its absence.
    if (!pushAvailable()) return null;
    const row = document.createElement('label');
    row.className = 'row';
    row.innerHTML = '<span>Notifications</span>';
    const btn = document.createElement('button');
    btn.className = 'btn';
    btn.textContent = deviceToken() ? 'On' : 'Turn on';
    btn.addEventListener('click', async () => {
      if (deviceToken()) {
        await disablePush();
        btn.textContent = 'Turn on';
        return;
      }
      const outcome = await enablePush();
      btn.textContent = outcome === 'granted' ? 'On' : 'Turn on';
      if (outcome === 'denied') toast('Notifications are off in Settings');
    });
    row.appendChild(btn);
    return row;
  },
};
